//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { IGalleryItem } from '@galleries/models/galleriesStateModel';
import FileId from '@models/FileId';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { isDefined } from '@utils/filterPredicates';

import { crl8 } from 'app/curalate.globals';

const EDGE_IMAGE_BASE = `${CURALATE_ENV.EDGE_URL}/v1/img`;

const EDGE_VIDEO_BASE = `${CURALATE_ENV.EDGE_URL}/v1/vid`;

function getMEDS_IMAGE_BASE() {
  return crl8.session && `${crl8.session.baseUrls.meds}/v1/img`;
}

function getMEDS_VIDEO_BASE() {
  return crl8.session && `${crl8.session.baseUrls.meds}/v1/vid`;
}

export function getMedsImageUrl(fileId: FileId) {
  return `${getMEDS_IMAGE_BASE()}/${fileId}`;
}

export function getMedsImageUrlWithParams(fileId: FileId, params: string) {
  return `${getMEDS_IMAGE_BASE()}/${fileId}/${params}`;
}

type MedsImageSize = 's' | 'm' | 'l' | 'xl';

export function getMedsSizedImageUrl(
  fileId: FileId,
  size: MedsImageSize,
  shouldUseEdge?: boolean,
) {
  return `${
    shouldUseEdge ? EDGE_IMAGE_BASE : getMEDS_IMAGE_BASE()
  }/${fileId}/d/${size}`;
}

/**
 * Shrinks the image such that the width and height are not larger than "size".
 */
export function getMedsBoundedImageUrl(fileId: FileId, size: number) {
  return `${getMEDS_IMAGE_BASE()}/${fileId}/b/${size}`;
}

export function getMedsSquareCropUrl(fileId: FileId, size: number) {
  return `${getMEDS_IMAGE_BASE()}/${fileId}/sc/${size}x${size}`;
}

export function getMedsSmartCropUrl(
  fileId: FileId,
  width: number,
  height: number,
) {
  return `${getMEDS_IMAGE_BASE()}/${fileId}/sc/${width}x${height}`;
}

export function getMedsSmartCropUrlByAspectRatio(
  fileId: FileId,
  aspectRatio: number,
) {
  return `${getMEDS_IMAGE_BASE()}/${fileId}/sc/${aspectRatio}`;
}

export function getMedsMaxWidthUrl(fileId: FileId, width: number) {
  return `${getMEDS_IMAGE_BASE()}/${fileId}/w/${width}`;
}

export function getMedsStandardVideoUrl(videoId: string) {
  return `${getMEDS_VIDEO_BASE()}/${videoId}/Standard`;
}

// Edge endpoints serve the same data as meds endpoints but are not behind a CDN
// Since our CDN has stricter CORS restrictions, sometimes edge is preferable
export function getEdgeFullHDVideoUrl(videoId: string, muteStatus: boolean) {
  return muteStatus
    ? `${EDGE_VIDEO_BASE}/${videoId}/FullHD-Muted`
    : `${EDGE_VIDEO_BASE}/${videoId}/FullHD`;
}

// Edge endpoints serve the same data as meds endpoints but are not behind a CDN
// Since our CDN has stricter CORS restrictions, sometimes edge is preferable
export function getEdgeOriginalVideoUrl(videoId: string, muteStatus: boolean) {
  return muteStatus
    ? `${EDGE_VIDEO_BASE}/${videoId}/Standard-Muted`
    : `${EDGE_VIDEO_BASE}/${videoId}/Original`;
}

async function fetchEdgeIsOriginalVideoAvailable(
  videoId: string,
  muteStatus: boolean,
): Promise<boolean> {
  const originalUrl = getEdgeOriginalVideoUrl(videoId, muteStatus);
  try {
    const result = await fetch(originalUrl, {
      method: 'HEAD',
    });
    return result.status === 200;
  } catch (e) {
    return false;
  }
}

export async function getHighestQualityAvailableVideoUrl(
  videoId: string,
  muteStatus: boolean,
): Promise<string> {
  return fetchEdgeIsOriginalVideoAvailable(videoId, muteStatus).then(
    (isOriginalAvailable) =>
      isOriginalAvailable
        ? getEdgeOriginalVideoUrl(videoId, muteStatus)
        : getEdgeFullHDVideoUrl(videoId, muteStatus),
  );
}

/*
 * Gets image url, converted to JPEG with the full resolution.
 *
 * Certain filetypes (i.e. Tiff, HEIC) aren't supported on all browsers, so when we want to display a fullsized
 * image we need to coerce it into a jpeg.
 */
export function getMedsFullSizedJpegUrl(fileId: FileId) {
  const base = getMEDS_IMAGE_BASE();
  return `${base}/${fileId}/b/10000?typ=jpeg`;
}

/**
 * An exceptionally common use case for the helpers in this file is
 * deriving an imageUrl from the mediaItem.networkItem.fileId. Here
 * are some helpers to accomplish that a bit easier.
 *
 * Uses the beautified image altFileId if it exists and
 * falls back to the network item fileId otherwise.
 */
export function getMediaMedsSizedImageUrl(
  item: IMlsMediaItem | IGalleryItem,
  size: MedsImageSize,
) {
  const networkItemFileId = item.networkItem?.fileId;
  const beautifiedFileId = item.beautifiedImageMetadata?.altFileId;

  const fileId = beautifiedFileId ?? networkItemFileId;
  if (fileId) {
    return getMedsSizedImageUrl(fileId, size);
  } else {
    return '';
  }
}

/**
 * Generates a URL for a smart-cropped image based on the provided dimensions.
 *
 * Uses the beautified image `altFileId` if it exists and falls back to the
 * network item's `fileId` otherwise.
 *
 * If neither `altFileId` nor `fileId` is available, an empty string is returned.
 *
 * @param {IMlsMediaItem | IGalleryItem} item - The media item containing the file information.
 * @param {number} width - The desired width of the cropped image.
 * @param {number} height - The desired height of the cropped image.
 * @returns {string} The URL of the smart-cropped image, or an empty string if no valid fileId is available.
 */
export function getMediaMedsSmartCropUrl(
  item: IMlsMediaItem | IGalleryItem,
  width: number,
  height: number,
) {
  const networkItemFileId = item.networkItem?.fileId;
  const beautifiedFileId = item.beautifiedImageMetadata?.altFileId;

  const fileId = beautifiedFileId ?? networkItemFileId;
  if (fileId) {
    return getMedsSmartCropUrl(fileId, width, height);
  } else {
    return '';
  }
}

/**
 * For use in html element `<video>`
 *
 * @param offsetInSeconds starts the video at this time
 */
export const getVideoUrlWithOffsetInSeconds = (
  videoUrl: string,
  offsetInSeconds: number,
) => `${videoUrl}#t=${offsetInSeconds}`;

/**
 * For use in getting the media used in Network scheduling Media components
 */
export const getNetworkVideoAndImageUrls = (mediaItem: IMlsMediaItem) => {
  const videoId = mediaItem.networkItem?.videoId;

  return {
    imageUrl: getMediaMedsSizedImageUrl(mediaItem, 'm'),
    videoUrl: isDefined(videoId) ? getMedsStandardVideoUrl(videoId) : undefined,
  };
};
